import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loyaltyAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Gift, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ReferidoRegistroPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await loyaltyAPI.publicReferralInfo(token);
        setInfo(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Link no válido');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !phone.trim() || phone.trim().length < 10) {
      toast.error('Ingresa tu nombre y un teléfono válido de 10 dígitos');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await loyaltyAPI.publicReferralSignup(token, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        birthday: birthday || null,
      });
      setSuccessData(data);
      toast.success('¡Bienvenido al programa de lealtad!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-lg font-semibold text-red-600">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>Ir al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (successData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-5">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                ¡Bienvenido!
              </h1>
              <div className="mt-4 rounded-xl bg-amber-50 border-2 border-amber-300 p-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-amber-700" />
                  <p className="font-bold text-amber-900">
                    Cupón: {successData.coupon_percent || 10}% de descuento
                  </p>
                </div>
                <p className="text-xs text-amber-800">
                  Válido en tu próximo servicio en <strong>{info?.business?.name}</strong>.
                  Menciónalo al llegar — el cajero verá tu cupón automáticamente cuando te busque en el POS.
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                {info?.referrer?.first_name || 'Quien te invitó'} recibirá su sello de recompensa
                cuando canjees tu cupón.
              </p>
            </div>
            <p className="text-[10px] text-slate-400">
              Guarda tu teléfono ({phone.slice(-4)}) — es tu identificador en el negocio.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200 bg-white">
        <CardContent className="p-8 space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-900 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-widest mt-3">
              {info?.business?.name}
            </p>
            <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Manrope' }}>
              Te invitaron al programa
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              <strong>{info?.referrer?.first_name} {info?.referrer?.last_name}</strong> te invita a{' '}
              {info?.business?.name}. Regístrate y obtén un <strong>cupón del 10% de descuento</strong> en tu próximo servicio.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nombre *</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  className="bg-slate-100 border-slate-200" data-testid="ref-first-name" />
              </div>
              <div className="space-y-1">
                <Label>Apellido</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                  className="bg-slate-100 border-slate-200" data-testid="ref-last-name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Teléfono (10 dígitos) *</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                inputMode="numeric" className="bg-slate-100 border-slate-200" data-testid="ref-phone" />
            </div>
            <div className="space-y-1">
              <Label>Cumpleaños (opcional)</Label>
              <Input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)}
                className="bg-slate-100 border-slate-200" data-testid="ref-birthday" />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11"
              data-testid="ref-submit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Crear mi cupón
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
